import { Component, OnInit, Input, Output, ViewChild, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NgForm,
  Validators
} from '@angular/forms';
import { omit, get } from 'lodash';
import { CommonRegEx } from '@nrwl/common-lib';
import { RadiusConfigWizardService } from '../../../services/radius.server/radius.server.service';
import { IComponentStateHistory, IValueChangedEvent } from '../../../wizard.content';
import { REMOVED } from '../../../services/base.wizard.step';
@Component({
  selector: 'nrwl-radius.server.configuration',
  templateUrl: './radius.server.configuration.component.html',
  styleUrls: ['./radius.server.configuration.component.scss']
})
export class RadiusServerConfigurationComponent implements OnInit {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() step: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() subStep: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() previousOutput: any;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm') form: NgForm;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  host: any;
  port: number;
  secret: string;
  accountingPort: number;
  nasIdentifier: string;
  accountingEnabled = false;
  radiusServerForm: UntypedFormGroup;
  constructor(private wizardService: RadiusConfigWizardService, private fb: UntypedFormBuilder) {}
  get portNumber(): AbstractControl {
    return this.radiusServerForm.get('port');
  }
  get hostname(): AbstractControl {
    return this.radiusServerForm.get('host');
  }
  get nasIdentifierInput(): AbstractControl {
    return this.radiusServerForm.get('nasIdentifier');
  }
  get secretInput(): AbstractControl {
    return this.radiusServerForm.get('secret');
  }
  get accountingPortInput(): AbstractControl {
    return this.radiusServerForm.get('accountingPort');
  }

  ngOnInit(): void {
    this.setValidation();
    this.step.form = this.form;
    if (this.wizardService.editMode) {
      this.resetData();
    }
    this.resetOutputFromParent();
  }

  hostUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'hostInput',
      key: 'host',
      newValue: this.host,
      isValid: this.radiusServerForm.controls.host.valid
    });
  }

  portUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'portInput',
      key: 'port',
      newValue: this.port,
      isValid: this.radiusServerForm.controls.port.valid
    });
  }

  accountingPortUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'accountingPortInput',
      key: 'accountingPort',
      newValue: this.accountingPort,
      isValid: this.radiusServerForm.controls.accountingPort.valid
    });
  }

  nasIdentifierUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'nasIdentifierInput',
      key: 'nasIdentifier',
      newValue: this.nasIdentifier,
      isValid: this.radiusServerForm.controls.nasIdentifier.valid
    });
  }

  secretUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'secretInput',
      key: 'secret',
      newValue: this.secret,
      isValid: this.radiusServerForm.controls.secret.valid
    });
  }

  accountingEnabledUpdated(): void {
    this.accountingEnabled = !this.accountingEnabled;
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'accountingEnabledInput',
      key: 'accountingEnabled',
      newValue: this.accountingEnabled,
      isValid: true
    });
    if (!this.accountingEnabled) {
      this.onDataValueChange.emit({
        componentId: this.componentId,
        inputId: 'accountingPortInput',
        key: 'accountingPort',
        newValue: REMOVED,
        isValid: true
      });
    } else {
      this.onDataValueChange.emit({
        componentId: this.componentId,
        inputId: 'accountingPortInput',
        key: 'accountingPort',
        newValue: this.accountingPort,
        isValid: this.radiusServerForm.controls.accountingPort.valid
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateHost(control: AbstractControl): any {
    if (control.value !== undefined && control.value !== null && control.value !== '') {
      if (!control.value.match(CommonRegEx.DNS_NAME)) {
        return { matchPattern: true };
      }
    }
  }

  setValidation(): void {
    this.radiusServerForm = this.fb.group({});
    this.radiusServerForm.addControl(
      'host',
      new UntypedFormControl('', [
        Validators.required,
        this.validateHost.bind(this),
        Validators.maxLength(80)
      ])
    );
    this.radiusServerForm.addControl(
      'port',
      new UntypedFormControl('', [Validators.required, Validators.min(1), Validators.max(65535)])
    );
    this.radiusServerForm.addControl(
      'accountingPort',
      new UntypedFormControl('', [Validators.required, Validators.min(1), Validators.max(65535)])
    );
    this.radiusServerForm.addControl('nasIdentifier', new UntypedFormControl('', [Validators.required]));
    this.radiusServerForm.addControl('secret', new UntypedFormControl('', [Validators.required]));
    this.radiusServerForm.addControl('accountingEnabled', new UntypedFormControl());
  }

  resetData(): void {
    this.wizardService.currentStep.output = omit(this.wizardService.currentStep.output, 'id');
    this.secret = get(this.previousOutput, 'secret');
    this.host = get(this.previousOutput, 'host');
    this.port = get(this.previousOutput, 'port');
    this.accountingPort = get(this.previousOutput, 'accountingPort');
    this.accountingEnabled = get(this.previousOutput, 'accountingEnabled');
    this.nasIdentifier = get(this.previousOutput, 'nasIdentifier');
    this.setRadiusServerForm();
  }

  setRadiusServerForm(): void {
    this.radiusServerForm.get('host').setValue(this.host, 'host');
    this.radiusServerForm.get('port').setValue(this.port, 'port');
    this.radiusServerForm.get('accountingPort').setValue(this.accountingPort, 'accountingPort');
    this.radiusServerForm.get('nasIdentifier').setValue(this.nasIdentifier, 'nasIdentifier');
    this.radiusServerForm.get('secret').setValue(this.secret, 'secret');
  }
  resetOutputFromParent(): void {
    const host = this.stateHistory(this.componentId, 'hostInput');
    if (host && host.value && host.value !== REMOVED) {
      this.host = host.value;
    }

    const port = this.stateHistory(this.componentId, 'portInput');
    if (port && port.value && port.value !== REMOVED) {
      this.port = port.value;
    }

    const nasIdentifier = this.stateHistory(this.componentId, 'nasIdentifierInput');
    if (nasIdentifier && nasIdentifier.value && nasIdentifier.value !== REMOVED) {
      this.nasIdentifier = nasIdentifier.value;
    }
    const secret = this.stateHistory(this.componentId, 'secretInput');
    if (secret && secret.value && secret.value !== REMOVED) {
      this.secret = secret.value;
    }

    const accountingEnabled = this.stateHistory(this.componentId, 'accountingEnabledInput');
    if (accountingEnabled && accountingEnabled.value && accountingEnabled.value !== REMOVED) {
      this.accountingEnabled = accountingEnabled.value;
    }

    const accountingPort = this.stateHistory(this.componentId, 'accountingPortInput');
    if (accountingPort && accountingPort.value && accountingEnabled.value !== REMOVED) {
      this.accountingPort = accountingPort.value;
    }
    this.setRadiusServerForm();
  }
}
